import { collection, addDoc, serverTimestamp, getDoc, doc } from "firebase/firestore";
import { db } from "../firebase";

/**
 * logModifica — registra una modifica con before/after nell'audit trail
 *
 * @param {object} params
 * @param {string} params.orgId
 * @param {string} params.uid           — chi ha fatto la modifica
 * @param {string} params.nomeUtente
 * @param {string} params.sezione       — "commesse"|"attivita"|"economia"|"dipendenti"|"noteSpese"|"clienti"
 * @param {string} params.refId         — id dell'oggetto modificato
 * @param {string} params.refNome       — nome leggibile dell'oggetto
 * @param {string} params.campo         — campo modificato (es. "stato", "valoreEconomico")
 * @param {string} params.campoLabel    — etichetta leggibile (es. "Stato commessa")
 * @param {*}      params.before        — valore prima
 * @param {*}      params.after         — valore dopo
 * @param {string} [params.projectId]
 * @param {string} [params.projectName]
 */
export async function logModifica({
  orgId, uid, nomeUtente,
  sezione, refId, refNome,
  campo, campoLabel,
  before, after,
  projectId="", projectName="",
}) {
  if(!orgId||!uid||!campo) return;
  if(before===after) return; // nessuna modifica reale

  try {
    await addDoc(collection(db,"auditLog"),{
      orgId, uid, nomeUtente: nomeUtente||"",
      sezione, refId, refNome: refNome||"",
      campo, campoLabel: campoLabel||campo,
      before: before!=null?String(before):"—",
      after: after!=null?String(after):"—",
      projectId, projectName,
      createdAt: serverTimestamp(),
    });
  } catch(e){
    console.warn("logModifica error:", e);
  }
}

/**
 * leggeERegistra — legge il doc prima, esegue l'updateDoc, poi logga
 * Helper per intercettare automaticamente before/after
 *
 * @param {object} params
 * @param {DocumentReference} params.docRef   — riferimento Firestore
 * @param {object}  params.updates            — campi da aggiornare
 * @param {object}  params.campiLabel         — { campo: "Etichetta leggibile" }
 * @param {object}  params.meta               — { orgId, uid, nomeUtente, sezione, refNome, projectId, projectName }
 */
export async function leggeERegistra({ docRef, updates, campiLabel={}, meta }) {
  try {
    // Leggi stato attuale
    const snap = await getDoc(docRef);
    const before = snap.exists() ? snap.data() : {};

    // Per ogni campo modificato registra before/after
    for(const [campo, valoreAfter] of Object.entries(updates)){
      if(campo==="updatedAt"||campo==="createdAt") continue;
      const valoreBefore = before[campo];
      if(valoreBefore!==valoreAfter){
        await logModifica({
          ...meta,
          refId: docRef.id,
          campo,
          campoLabel: campiLabel[campo]||campo,
          before: valoreBefore,
          after: valoreAfter,
        });
      }
    }
  } catch(e){
    console.warn("leggeERegistra error:", e);
  }
}

// ── Etichette campi comuni ─────────────────────────────────
export const CAMPI_LABEL = {
  // Commesse / Progetti
  status:           "Stato",
  archived:         "Archiviata",
  valoreEconomico:  "Valore economico",
  dataInizio:       "Data inizio",
  dataFine:         "Data fine",
  name:             "Nome",
  description:      "Descrizione",
  operaiNecessari:  "Operai necessari",
  livelloMin:       "Livello min. richiesto",
  // Attività
  title:            "Titolo attività",
  assignedTo:       "Assegnato a",
  assignedName:     "Responsabile",
  dueDate:          "Scadenza",
  priority:         "Priorità",
  // Economia
  costoManodopera:  "Costo manodopera",
  costoMateriali:   "Costo materiali",
  ricavi:           "Ricavi",
  // Dipendenti
  costoOrario:      "Costo orario",
  ruolo:            "Ruolo",
  livelloProfessionalita: "Livello professionalità",
  mansione:         "Mansione",
  // Note spese
  stato:            "Stato",
  // Clienti
  ragioneSociale:   "Ragione sociale",
  piva:             "P.IVA",
};
